// Basic
import { useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import useCustomPayStore from '@/app/store/customPay';
import useSessionStore from '@/app/store/session';
import useRootOrganizationStore from '@/app/store/rootOrganization';
import actionsSims from '../../api/actionsSims';
// Components
import ModalUpdateSimCard from '../modals/ModalUpdateSimCard';
import ModalAskConfirmation from '@/common-utils/components/renders/modal/components/modals/ModalAskConfirmation';
import ModalReplaceSimCard from '../modals/ModalReplaceSimCard';
import ModalActivateSimCard from '../modals/activateSimCard/ModalActivateSimCard';
import ModalUpdateSimPlan from '../modals/ModalUpdateSimPlan';
import Entity from '@/common-utils/components/renders/entity/Entity';
import Dropdown from '../../../../../common-utils/components/renders/dropdown/Dropdown';
import CustomPayModalActivateSimCard from '../modals/activateSimCard/CustomPayModalActivateSimCard';
// Common
import ICONS from '../../../../../common-utils/enums/icons';
import Permissions from '@/app/permissions/Permissions';
import RouteUrls from '@/app/root/enums/RouteUrls';
import simCardFields from '../../fields/simCardFields';
import generateSimCardActionsLifecycle from './actions/generateSimCardActionsLifecycle';
import getObjectAttributeFromStringKey from '@/common-utils/functions/getObjectAttributeFromStringKey';
import generateNestedFieldAccessor from '@/common-utils/functions/generateNestedFieldAccessor';
import SIM_OFFERS_PROVIDERS from '@/app/modules/sims/enums/SimOffersProviders';
import SIM_CARDS_STATUS from '../../enums/SimCardStatus';
import { buildUrl } from '@/common-utils/functions/restUtils';
import { toast } from '@/common-utils/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
// Lang
import { nsSimCard } from '@/app/i18n/namespaces';
import hasRequiredPermissions from '@/common-utils/functions/hasRequiredPermissions';
import historicsFields from '@/app/modules/historics/fields/historicsFields';
import TAB_KEYS_HISTORICS from '@/app/root/enums/tabs/HistoricsTabs';
/**
 * @param root0
 * @param root0.simCard
 * @param root0.setLaunchRefetch
 * @returns {JSX} DropdownSimCardActionsRow
 */
function DropdownSimCardActionsRow({ simCard, setLaunchRefetch }) {

    // Router
    const navigate = useNavigate();

    // Store
    const isCustomPay = useCustomPayStore((s) => s.isCustomPay);
    const rootOrganization = useRootOrganizationStore(s => s.rootOrganization);
    const permissions = useSessionStore(s => s.session.permissions);

    // State
    const [showModalActivateSimCard, setShowModalActivateSimCard] = useState(false);
    const handleShowModalActivateSimCard = () => {
        setShowModalActivateSimCard(true);
    };
    const [showCustomPayModalActivateSimCard, setShowCustomPayModalActivateSimCard] = useState(false);
    const handleShowCustomPayModalActivateSimCard = () => {
        setShowCustomPayModalActivateSimCard(true);
    };
    const [showModalAskConfirmationReactivateSimCard, setShowModalAskConfirmationReactivateSimCard] = useState(false);
    const handleShowModalAskConfirmationReactivateSimCard = () => {
        setShowModalAskConfirmationReactivateSimCard(true);
    };
    const [showModalUpdateSimCard, setShowModalUpdateSimCard] = useState(false);
    const handleShowModalUpdateSimCard = () => {
        setShowModalUpdateSimCard(true);
    };
    const [showModalUpdateSimPlan, setShowModalUpdateSimPlan] = useState(false);
    const handleShowModalUpdateSimPlan = () => {
        setShowModalUpdateSimPlan(true);
    };
    const [showModalAskConfirmationTerminateSimCard, setShowModalAskConfirmationTerminateSimCard] = useState(false);
    const handleShowModalAskConfirmationTerminateSimCard = () => {
        setShowModalAskConfirmationTerminateSimCard(true);
    };
    const [showModalAskConfirmationSuspendSimCard, setShowModalAskConfirmationSuspendSimCard] = useState(false);
    const handleShowModalAskConfirmationSuspendSimCard = () => {
        setShowModalAskConfirmationSuspendSimCard(true);
    };
    const [showModalReplaceSimCard, setShowModalReplaceSimCard] = useState(false);
    const handleShowModalReplaceSimCard = () => {
        setShowModalReplaceSimCard(true);
    };

    // API Calls suspend SimCard
    const onSuccessSuspendSimCard = () => {
        toast({
            description: <Entity entity={'modal.suspend.success'} namespace={nsSimCard} />,
            variant: 'success'
        });
        setLaunchRefetch(true);
    };
    const onErrorSuspendSimCard = () => {
        setShowModalAskConfirmationSuspendSimCard(false);
        toast({
            description: <Entity entity={'modal.suspend.error'} namespace={nsSimCard} />,
            variant: 'danger'
        });
    };
    const submitSuspendSimCard = actionsSims.suspendSimCard(onSuccessSuspendSimCard, onErrorSuspendSimCard);

    // API Calls terminate SimCard
    const onSuccessTerminateSimCard = () => {
        toast({
            description: <Entity entity={'modal.terminate.success'} namespace={nsSimCard} />,
            variant: 'success'
        });
        setLaunchRefetch(true);
    };
    const onErrorTerminateSimCard = () => {
        setShowModalAskConfirmationTerminateSimCard(false);
        toast({
            description: <Entity entity={'modal.terminate.error'} namespace={nsSimCard} />,
            variant: 'danger'
        });
    };
    const submitTerminateSimCard = actionsSims.terminateSimCard(onSuccessTerminateSimCard, onErrorTerminateSimCard);

    // API Calls reactivate SimCard
    const onSuccessReactivateSimCard = () => {
        toast({
            description: <Entity entity={'modal.reactivate.success'} namespace={nsSimCard} />,
            variant: 'success'
        });
        setLaunchRefetch(true);
    };
    const onErrorReactivateSimCard = () => {
        setShowModalAskConfirmationReactivateSimCard(false);
        toast({
            description: <Entity entity={'modal.reactivate.error'} namespace={nsSimCard} />,
            variant: 'danger'
        });
    };
    const submitReactivateSimCard = actionsSims.reactivateSimCard(onSuccessReactivateSimCard, onErrorReactivateSimCard);

    // Constants
    const isIngenico = rootOrganization.uuid === '4ac85a69-8e6e-4f8f-adbb-4c62aba60dd6';
    const isSimReplaceable = getObjectAttributeFromStringKey(
        simCard,
        generateNestedFieldAccessor(simCardFields.SIM_CARD.ICCID_OPERATOR)
    )[0] === SIM_OFFERS_PROVIDERS.BOUYGUES_TELECOM.name;
    const isSimActivated = simCard ?
        getObjectAttributeFromStringKey(simCard, generateNestedFieldAccessor(simCardFields.SIM_CARD.STATUS)) === SIM_CARDS_STATUS.ACTIVATED.name : false;
    const isPlanUpdatable = simCard ?
        getObjectAttributeFromStringKey(simCard, generateNestedFieldAccessor(simCardFields.SIM_CARD.NEXT_OFFER)) === null
        && getObjectAttributeFromStringKey(simCard, generateNestedFieldAccessor(simCardFields.SIM_CARD.OFFER_CAN_BE_UPDATED)) === true : false;
    const simCardId = simCard ? simCard.sim.uuid : null;
    const organizationLegalName = simCard ? getObjectAttributeFromStringKey(simCard, generateNestedFieldAccessor(simCardFields.CLIENT.LEGAL_NAME)) : null;

    return (
        <>
            <Dropdown
                triggerValues={{ variant: 'ghostNoBorder', icon: ICONS.MORE }}
                dataCy='sim-cards-actions-dropdown-row'
                actions={[
                    ...generateSimCardActionsLifecycle(
                        simCard.sim,
                        permissions,
                        {
                            isCustomPay,
                            isSimActivated,
                            isPlanUpdatable,
                            shouldShowPayviewAction: isCustomPay,
                            shouldShowTerminateAction: isCustomPay && isIngenico || !isCustomPay,
                            shouldShowReplaceSimAction: isSimReplaceable,
                            activateSimCard: isCustomPay ? handleShowCustomPayModalActivateSimCard : handleShowModalActivateSimCard,
                            reactivateSimCard: handleShowModalAskConfirmationReactivateSimCard,
                            terminateSimCard: handleShowModalAskConfirmationTerminateSimCard,
                            suspendSimCard: handleShowModalAskConfirmationSuspendSimCard,
                            replaceSimCard: handleShowModalReplaceSimCard,
                            updateLabel: handleShowModalUpdateSimCard,
                            updatePlan: handleShowModalUpdateSimPlan
                        }
                    ),
                    {
                        icon: ICONS.HISTORY,
                        namespace: nsSimCard,
                        entity: 'actions.row.historics_access',
                        onClick: () => {
                            const simCardIccid = simCard ? getObjectAttributeFromStringKey(
                                simCard,
                                generateNestedFieldAccessor(simCardFields.SIM_CARD.ICCID)
                            ) : null;
                            const simCardHistorics = buildUrl(RouteUrls.HISTORICS.url,
                                {
                                    view: TAB_KEYS_HISTORICS.SIM_CARDS.name,
                                    filters: simCardIccid ? [
                                        {
                                            key: historicsFields.SIM_CARDS_HISTORICS.SIM_ICCID.key,
                                            value: simCardIccid
                                        }
                                    ] : []
                                }
                            );
                            navigate(simCardHistorics);
                        },
                        visible: true,
                        disabled: !hasRequiredPermissions({
                            permissions: permissions,
                            requiredPermissions: [Permissions.LIST_HISTORY.name]
                        }),
                        tooltipForbidden: 'title.permissions.access_denied'
                    }
                ]}

            />
            {
                showModalActivateSimCard &&
                <ModalActivateSimCard
                    isOpen={showModalActivateSimCard}
                    setIsOpen={setShowModalActivateSimCard}
                    simCardId={simCardId}
                    setLaunchRefetch={setLaunchRefetch}
                />
            }
            {
                showCustomPayModalActivateSimCard &&
                <CustomPayModalActivateSimCard
                    isOpen={showCustomPayModalActivateSimCard}
                    setIsOpen={setShowCustomPayModalActivateSimCard}
                    simCard={simCard.sim}
                    setLaunchRefetch={setLaunchRefetch}
                />
            }
            {
                showModalAskConfirmationReactivateSimCard &&
                <ModalAskConfirmation
                    entityTitle='modal.reactivate.title'
                    entityBody='modal.reactivate.text'
                    confirmChoice={() => {
                        submitReactivateSimCard.mutate({
                            uuids: Array.isArray(simCardId) ? simCardId : [simCardId]
                        });
                    }}
                    namespace={nsSimCard}
                    isLoading={submitReactivateSimCard.isLoading}
                    isOpen={showModalAskConfirmationReactivateSimCard}
                    setIsOpen={setShowModalAskConfirmationReactivateSimCard}
                />
            }
            {
                showModalUpdateSimCard &&
                <ModalUpdateSimCard
                    isOpen={showModalUpdateSimCard}
                    setIsOpen={setShowModalUpdateSimCard}
                    sim={simCard.sim}
                    setLaunchRefetch={setLaunchRefetch}
                />
            }
            {
                showModalUpdateSimPlan &&
                <ModalUpdateSimPlan
                    isOpen={showModalUpdateSimPlan}
                    setIsOpen={setShowModalUpdateSimPlan}
                    simCardId={simCardId}
                    organizationName={organizationLegalName}
                    setLaunchRefetch={setLaunchRefetch}
                />
            }
            {
                showModalReplaceSimCard &&
                <ModalReplaceSimCard
                    isOpen={showModalReplaceSimCard}
                    setIsOpen={setShowModalReplaceSimCard}
                    simCard={simCard}
                    setLaunchRefetch={setLaunchRefetch}
                />
            }
            {
                showModalAskConfirmationTerminateSimCard &&
                <ModalAskConfirmation
                    entityTitle='modal.terminate.title'
                    entityBody='modal.terminate.text'
                    confirmChoice={() => {
                        submitTerminateSimCard.mutate({
                            uuids: Array.isArray(simCardId) ? simCardId : [simCardId]
                        });
                    }}
                    namespace={nsSimCard}
                    isLoading={submitTerminateSimCard.isLoading}
                    isOpen={showModalAskConfirmationTerminateSimCard} setIsOpen={setShowModalAskConfirmationTerminateSimCard}
                />
            }
            {
                showModalAskConfirmationSuspendSimCard &&
                <ModalAskConfirmation
                    entityTitle='modal.suspend.title'
                    entityBody='modal.suspend.text'
                    confirmChoice={() => {
                        submitSuspendSimCard.mutate({
                            uuids: Array.isArray(simCardId) ? simCardId : [simCardId]
                        });
                    }}
                    namespace={nsSimCard}
                    isLoading={submitSuspendSimCard.isLoading}
                    isOpen={showModalAskConfirmationSuspendSimCard} setIsOpen={setShowModalAskConfirmationSuspendSimCard}
                />
            }

        </>
    );
}

DropdownSimCardActionsRow.propTypes = {
    simCard: PropTypes.shape({
        sim: PropTypes.shape({
            uuid: PropTypes.string
        })
    }),
    setLaunchRefetch: PropTypes.func
};
export default DropdownSimCardActionsRow;